import { type InputHTMLAttributes } from 'react'
import classnames from 'classnames'
import { Icon } from '@atoms'

export type CheckboxProps = InputHTMLAttributes<HTMLInputElement> & {
    name: string
    label?: React.ReactNode
    itemWrapperClassname?: string
    labelWrapperClassname?: string
    isBoxed?: boolean
    indeterminate?: boolean
}

export const Checkbox: React.FC<CheckboxProps> = (props) => {
    const {
        disabled,
        label,
        name,
        checked,
        isBoxed,
        itemWrapperClassname = '',
        indeterminate,
        onChange,
    } = props

    const baseCheckboxClassname = classnames([
        'flex h-4 w-4 my-1 items-center justify-center rounded',
        'border border-neutral-300 hover:border-neutral-400 active:border-neutral-500',
        'outline outline-0 outline-white/0',
        'peer-focus:outline peer-focus:outline-4 peer-focus:outline-neutral-400/25',
    ])
    const checkboxClassname = classnames(
        baseCheckboxClassname,
        'bg-white',
        // Checked
        'peer-checked:bg-azure peer-checked:border-azure peer-checked:text-white',
        'peer-checked:hover:bg-blue-600 peer-checked:hover:border-blue-600',
        'peer-checked:active:bg-blue-700 peer-checked:active:border-blue-700',
        'peer-checked:peer-focus:outline-4 peer-checked:peer-focus:outline-azure/25',
        '[&_.u-icon]:opacity-0 peer-checked:[&_.u-icon]:opacity-100'
    )
    const indeterminateClassname = classnames(
        baseCheckboxClassname,
        'bg-azure border-azure text-white',
        'hover:bg-blue-600 hover:border-blue-600',
        'active:bg-blue-700 active:border-blue-700',
        'peer-focus:outline-4 peer-focus:outline-azure/25',
        '[&_.u-icon]:opacity-100'
    )

    const boxedWrapperClassnames = classnames(
        'px-3 py-1',
        'hover:bg-blue-50',
        'active:bg-blue-100',
        'rounded'
    )

    const wrapperClassnames = classnames(
        // necessary for properly positioning input element and not cause position or scroll issues
        // use of sr-only instead of hidden allows for focus and tab navigation
        'relative',
        'flex items-start gap-2',
        isBoxed && boxedWrapperClassnames,
        itemWrapperClassname,
        {
            'cursor-pointer': !disabled,
        }
    )

    const labelWrapperClassname = classnames(
        'text-label-small',
        props.labelWrapperClassname
    )

    const dataProps = indeterminate
        ? {
              'data-indeterminate': true,
          }
        : {}

    return (
        <label
            className={wrapperClassnames}
            onClick={(event) => {
                event.stopPropagation()
            }}
        >
            <input
                {...dataProps}
                className="peer sr-only"
                type="checkbox"
                name={name}
                disabled={disabled}
                checked={checked}
                onChange={onChange}
                data-testid={name}
            />
            {indeterminate ? (
                <div className={indeterminateClassname}>
                    <Icon name={'remove'} size="xsmall" className="u-icon" />
                </div>
            ) : (
                <div className={checkboxClassname}>
                    <Icon name={'check'} size="xsmall" className="u-icon" />
                </div>
            )}

            {label && <span className={labelWrapperClassname}>{label}</span>}
        </label>
    )
}
